import { InputLabel } from '@/components/fhirEngine/inputLabel/InputLabel'
import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { pdfjs } from 'react-pdf'
import {
  Attachment,
  AttachmentInputProps
} from '@healthnz-prm/fhir-engine-renderer/types'
import { Box, Button, InputMessage, Stack } from '@te-whatu-ora/anatomic'
import { FilePreview } from './FilePreview'
import { useUploadAttachment } from '@/hooks/useUploadAttachment'
import { useParams } from 'react-router-dom'
import { useAuth } from '@/contexts/auth/auth'
import { SUPPORTED_ATTACHMENT_MIME_TYPES } from '@/lib/constants'
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`

export interface InputAttachmentProps {
  onChange?: (value: File) => void
  value?: File
}

export function AttachmentInput(props: AttachmentInputProps) {
  const { disabled, error, helperText, inputId, onChange, value } = props

  const authToken = useAuth()
  const { questionnaireId } = useParams()
  const [file, setFile] = useState<File | null>(null)
  const [validationError, setValidationError] = useState<string | undefined>(
    undefined
  )

  const {
    uploadAttachment,
    attachmentReference,
    loading: uploadLoading,
    error: uploadError
  } = useUploadAttachment({
    questionnaireId,
    authToken
  })

  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0]
    if (!file || !SUPPORTED_ATTACHMENT_MIME_TYPES.includes(file.type)) {
      setValidationError('Unsupported file type')
      return
    }
    setValidationError(undefined)
    uploadAttachment(file)
    setFile(file)
  }

  const openFileDialog = () => {
    if (fileInputRef.current) fileInputRef.current.click()
  }

  useEffect(() => {
    if (attachmentReference)
      onChange?.({
        title: file?.name || '',
        contentType: file?.type || '',
        language: 'en-NZ',
        url: attachmentReference
      } as Attachment)
  }, [attachmentReference, file?.name, file?.type, onChange])

  // The input is unmounted when the page changes, so a file picked earlier is
  // gone while the attachment it produced is still in form state.
  const selectedFileName = file?.name || value?.title
  const hasAttachment = Boolean(file || value)

  return (
    <Box role='group' aria-labelledby={inputId}>
      <InputLabel {...props} />
      <Stack space='xsmall'>
        <InputMessage helperText='Note: only image and PDF files are supported' />
        <FilePreview attachment={value} file={file} isLoading={uploadLoading} />
        <input
          disabled={disabled || uploadLoading}
          id={inputId}
          onChange={handleFileChange}
          ref={fileInputRef}
          style={{ display: 'none' }}
          type='file'
        />
        <Button
          aria-describedby={
            selectedFileName
              ? `Replace selected file: ${selectedFileName}`
              : 'Upload file'
          }
          disabled={disabled || uploadLoading}
          icon='arrow_up'
          iconPosition='left'
          onPress={openFileDialog}
          variant='tertiary'
        >
          {hasAttachment ? 'Replace file' : 'Upload file'}
        </Button>
      </Stack>
      <InputMessage
        errorMessage={validationError || uploadError || error}
        helperText={helperText}
      />
    </Box>
  )
}
